"use client";
import React, { useRef } from "react";
import {
  motion,
  useScroll,
  useTransform,
  useSpring,
} from "framer-motion";
import { cn } from "@/lib/utils";

interface ContainerScrollProps {
  titleComponent: string | React.ReactNode;
  children: React.ReactNode;
  className?: string;
  preserveDesign?: boolean;
}

export const ContainerScrollAnimation = ({
  titleComponent,
  children,
  className,
  preserveDesign = false,
}: ContainerScrollProps) => {
  const containerRef = useRef<HTMLDivElement>(null);
  const { scrollYProgress } = useScroll({
    target: containerRef,
  });
  const [isMobile, setIsMobile] = React.useState(false);

  React.useEffect(() => {
    const checkMobile = () => {
      setIsMobile(window.innerWidth <= 768);
    };
    checkMobile();
    window.addEventListener("resize", checkMobile);
    return () => {
      window.removeEventListener("resize", checkMobile);
    };
  }, []);

  const scaleDimensions = () => {
    return isMobile ? [0.7, 0.9] : [1.05, 1];
  };

  const rotate = useTransform(scrollYProgress, [0, 1], [20, 0]);
  const scale = useTransform(scrollYProgress, [0, 1], scaleDimensions());
  const translate = useTransform(scrollYProgress, [0, 1], [0, -100]);

  const opacity = useTransform(scrollYProgress, [0, 0.5, 1], [0, 0.5, 1]);

  const rotateSpring = useSpring(rotate, {
    stiffness: 100,
    damping: 30,
    restDelta: 0.001,
  });

  const scaleSpring = useSpring(scale, {
    stiffness: 100,
    damping: 30,
    restDelta: 0.001,
  });

  const translateSpring = useSpring(translate, {
    stiffness: 100,
    damping: 30,
    restDelta: 0.001,
  });

  return (
    <div
      className={cn("h-[60rem] md:h-[80rem] flex items-center justify-center relative p-2 md:p-20", className)}
      ref={containerRef}
    >
      <div
        className="py-10 md:py-40 w-full relative"
        style={{
          perspective: "1000px",
        }}
      >
        {titleComponent && <Header scrollYProgress={scrollYProgress} titleComponent={titleComponent} />}
        {preserveDesign ? (
          <motion.div
            style={{
              rotateX: rotateSpring,
              scale: scaleSpring,
              translateY: translateSpring,
            }}
          >
            {children}
          </motion.div>
        ) : (
          <Card
            rotate={rotateSpring}
            scale={scaleSpring}
            translate={translateSpring}
          >
            {children}
          </Card>
        )}
      </div>
    </div>
  );
};

export const Header = ({ scrollYProgress, titleComponent }: { scrollYProgress: any; titleComponent: string | React.ReactNode }) => {
  const y = useTransform(scrollYProgress, [0, 1], [0, -200]);
  const opacity = useTransform(scrollYProgress, [0, 0.5, 1], [1, 0.5, 0]);

  return (
    <motion.div
      style={{
        y,
        opacity,
      }}
      className="div max-w-2xl mx-auto text-center"
    >
      {typeof titleComponent === "string" ? (
        <h2 className="text-3xl md:text-4xl font-bold text-neutral-800 dark:text-neutral-200">
          {titleComponent}
        </h2>
      ) : (
        titleComponent
      )}
    </motion.div>
  );
};

export const Card = ({
  rotate,
  scale,
  translate,
  children,
}: {
  rotate: any;
  scale: any;
  translate: any;
  children: React.ReactNode;
}) => {
  return (
    <motion.div
      style={{
        rotateX: rotate,
        scale,
        boxShadow:
          "0 0 #0000004d, 0 9px 20px #0000004a, 0 37px 37px #00000042",
      }}
      className="max-w-5xl -mt-12 mx-auto h-[30rem] md:h-[40rem] w-full border-4 border-[#6C6C6C] p-2 md:p-6 bg-[#222222] rounded-[30px] shadow-2xl"
    >
      <div className="h-full w-full overflow-hidden rounded-2xl bg-gray-100 dark:bg-zinc-900 md:rounded-2xl">
        {children}
      </div>
    </motion.div>
  );
};

